/**
 * D196: authored verbs — what a pack-declared verb DOES to a thing.
 *
 * @remarks
 * Before D196 a verb a pack declared for itself could only ever refuse: the
 * engine narrows the verbs it knows (`take`, `use`, `open`, …), refuses the
 * ones declared unsupported by name (D35/D47), and answers everything else
 * with `unknown_intent`. So "say her name", "kneel at the ground", "pour the
 * water" — the acts a game is actually about — had to be spelled as
 * `use X on Y` or not exist. This is the general primitive `useInteractions`
 * is the two-object sugar for: a verb applied to ONE thing, gated by
 * conditions, producing effects and narrative, executed as a real turn
 * (`command_executed`, so triggers fire and replays replay it).
 *
 * Keyed by CANONICAL verb token, exactly as `unsupportedVerbNarratives` is
 * (one entry answers every alias). Which keys are legal is a vocabulary
 * question the schema cannot answer alone — the verb must be declared,
 * supported, canonical, and NOT one the engine's own roster already
 * narrows — and `content-loader` owns it, as it owns the D47 rule.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * One key of a per-item verb table: a canonical verb token, lowercase.
 *
 * @remarks
 * Shared by `unsupportedVerbNarratives` (D47) and `verbInteractions` (D196)
 * — the lookup compares against the token the vocabulary canonicalized to,
 * so an uppercase key, or an alias, would silently never match.
 */
export const canonicalVerbKeySchema = z
	.string()
	.trim()
	.min(1)
	.refine((key) => key === key.toLowerCase(), {
		message:
			'verb keys must be lowercase canonical verb tokens — the lookup uses the canonicalized token, so an uppercase key can never match',
	});

/**
 * One arm of an authored verb: applying THIS verb to the item, gated by
 * conditions, producing effects and narrative. The `useInteractions` shape
 * without a target — the item it sits on is the object. `narratives` is
 * min-1 for the same reason as there: a NARRATIVE-ONLY arm is real authored
 * fiction, and is how an author writes the refusal that answers when the
 * conditions of the arms above it do not hold.
 */
export const verbInteractionSchema = z
	.strictObject({
		conditions: z.array(conditionSchema).readonly().default([]),
		effects: z.array(effectSchema).readonly().default([]),
		narratives: z.array(narrativeLineSchema).min(1).readonly(),
	})
	.readonly();

/** One authored verb arm — see {@link verbInteractionSchema}. */
export type VerbInteraction = z.infer<typeof verbInteractionSchema>;

/**
 * An item's authored verbs: canonical token → its arms, searched in authored
 * order; the first whose conditions all hold speaks and acts.
 */
export const verbInteractionsSchema = z
	.record(canonicalVerbKeySchema, z.array(verbInteractionSchema).min(1).readonly())
	.readonly();

/** A pack's per-item authored verbs — see {@link verbInteractionsSchema}. */
export type VerbInteractions = z.infer<typeof verbInteractionsSchema>;

/** One arm of an item's authored verbs, with the path a report would name it by. */
export interface LocatedVerbInteraction {
	readonly arm: VerbInteraction;
	/** `verbInteractions.<verb>[<index>]`, relative to the item. */
	readonly path: string;
	readonly verbToken: string;
}

/**
 * Every arm of an authored verb table, in table order, each with its path —
 * the one flattening every walker over the table shares (the loader's
 * reference checks and effect sources, the audit's readers).
 *
 * @param verbInteractions - The table, or nothing
 * @param field - The field the table lives in: an item's or a body's `verbInteractions`, or (D252) a body's `orders`
 * @returns The arms, located
 */
export const verbInteractionArmsOf = (
	verbInteractions: VerbInteractions | undefined,
	field: 'orders' | 'verbInteractions' = 'verbInteractions',
): readonly LocatedVerbInteraction[] =>
	Object.entries(verbInteractions ?? {}).flatMap(([verbToken, arms]) =>
		arms.map((arm, index): LocatedVerbInteraction => ({ arm, path: `${field}.${verbToken}[${String(index)}]`, verbToken })),
	);
